import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import {
  createDiscountCodeService,
  getDiscountCouponsService,
  updateDiscountCodeModal,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const CouponModal = ({ isOpen, onClose, coupon, onSave, mode = "create" }) => {
  const dispatch = useDispatch();

  const { categoryList } = useSelector((state) => state.categories);

  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    type: "ratio",
    value: "",
    start_date: "",
    end_date: "",
    usage_limit: "",
    number_of_uses_for_user: "",
    condition: "above_certain_amount",
    /*certain_amount: "",
    category_id: "",*/
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (coupon && mode === "edit") {
      setFormData({
        ...coupon,
        category_id	:coupon?.category_id?.id,
        start_date: coupon?.start_date
          ? new Date(coupon.start_date)?.toISOString()?.split("T")?.[0]
          : "",
        end_date: coupon?.end_date
          ? new Date(coupon.end_date)?.toISOString()?.split("T")?.[0]
          : "",
      });
    }
    setErrors({});
  }, [coupon, mode, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.value) {
      newErrors.value = "Value is required";
    } else if (formData?.type === "ratio") {
      const value = parseFloat(formData.value);
      if (value < 0 || value > 100) {
        newErrors.value = "Must be between 0-100%";
      }
    }

    if (!formData?.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!formData?.end_date) {
      newErrors.end_date = "End date is required";
    } else if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      newErrors.end_date = "Must be after start date";
    }

    if (formData?.usage_limit && formData?.usage_limit < 0) {
      newErrors.usage_limit = "Must be ≥ 0";
    }

    if (
      formData?.number_of_uses_for_user &&
      formData?.number_of_uses_for_user < 0
    ) {
      newErrors.number_of_uses_for_user = "Must be ≥ 0";
    }

    if (
      formData.condition === "above_certain_amount" &&
      !formData.certain_amount
    ) {
      newErrors.certain_amount = "Required amount";
    }

    if (
      formData.condition === "on_specific_category" &&
      !formData.category_id
    ) {
      newErrors.category_id = "Required category";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      console.log(formData);
      if (mode === "create") {
        dispatch(createDiscountCodeService(formData)).then((response) => {
          if (
            response?.payload?.status == 200 ||
            response?.payload?.status == 201
          ) {
            toast("added success");
            dispatch(getDiscountCouponsService({ page: 1 }));
            onClose();
          }
        });
      } else {
        dispatch(
          updateDiscountCodeModal({ id: coupon?.uuid, formData: formData })
        ).then((response) => {
          if (
            response?.payload?.status == 200 ||
            response?.payload?.status == 201
          ) {
            toast("updated success");
            dispatch(getDiscountCouponsService({ page: 1 }));
            onClose();
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm bg-black/30 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto transform transition-all duration-200 ease-out">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {mode === "create" ? t("createCoupon") : t("editCoupon")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {mode === "create"
                ? t("setupDiscounts")
                : t("updateCouponDetails")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Discount Type Section */}
          <div className="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Tag" size={18} className="text-primary-500" />
             {t("discountSettings")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 {t("discountType")}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      className="h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                      checked={formData.type === "ratio"}
                      onChange={() => handleInputChange("type", "ratio")}
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                       {t("percentage")}
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      className="h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                      checked={formData.type === "fixed_value"}
                      onChange={() => handleInputChange("type", "fixed_value")}
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("fixedAmount")}
                    </span>
                  </label>
                </div>
              </div>

              {/* Value Input */}
              <Input
                label={
                  formData.type === "ratio" ? t("percentageSymbol") : t("fixedAmountSymbol")
                }
                type="number"
                value={formData.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                error={errors.value}
                min={formData.type === "ratio" ? "0" : "0.01"}
                max={formData.type === "ratio" ? "100" : undefined}
                step={formData.type === "ratio" ? "1" : "0.01"}
                required
                className="w-full"
                prefix={formData.type === "ratio" ? "%" : "$"}
              />
            </div>
          </div>

          {/* Validity Section */}
          <div className="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Calendar" size={18} className="text-primary-500" />
             {t("validityPeriod")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label=  {t("startDate")}
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  handleInputChange("start_date", e.target.value)
                }
                error={errors.start_date}
                required
                min={new Date().toISOString().split("T")[0]}
                icon="Calendar"
              />

              <Input
                label={t("endDate")}
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange("end_date", e.target.value)}
                error={errors.end_date}
                required
                min={
                  formData.start_date || new Date().toISOString().split("T")[0]
                }
                icon="Calendar"
              />
            </div>
          </div>

          {/* Usage Limits Section */}
          <div className="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Users" size={18} className="text-primary-500" />
              {t("usageLimits")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label={t("globalUsageLimit")}
                type="number"
                placeholder={t("unlimitedIfBlank")}
                value={formData.usage_limit}
                onChange={(e) =>
                  handleInputChange("usage_limit", e.target.value)
                }
                error={errors.usage_limit}
                min="0"
                icon="Globe"
              />

              <Input
                label={t("perUser Limit")}
                type="number"
              //  placeholder={t("perUser Limit")}
                value={formData.number_of_uses_for_user}
                onChange={(e) =>
                  handleInputChange("number_of_uses_for_user", e.target.value)
                }
                error={errors.number_of_uses_for_user}
                min="0"
                icon="User"
              />
            </div>
          </div>

          {/* Conditions Section */}
          <div className="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Filter" size={18} className="text-primary-500" />
              {t("conditions")}
            </h3>

            <Select
              label= {t("applyCondition")}
              options={[
                {
                  value: "above_certain_amount",
                  label: "above_certain_amount",
                },
                {
                  value: "on_specific_category",
                  label: "on_specific_category",
                },
              ]}
              value={formData.condition}
              onChange={(value) => handleInputChange("condition", value)}
              required
              icon="Filter"
            />

            {formData.condition === "above_certain_amount" && (
              <Input
                label={t("minimumOrderAmount")}
                type="number"
                placeholder={t("enter_minimum_amount")}
                value={formData.certain_amount}
                onChange={(e) =>
                  handleInputChange("certain_amount", e.target.value)
                }
                error={errors.certain_amount}
                min="0"
                step="0.01"
                required={formData.condition === "above_certain_amount"}
                icon="DollarSign"
                className="mt-4"
              />
            )}

            {formData.condition === "on_specific_category" && (
              <Select
                name={t("selectCategoryPlaceholder")}
                value={formData.category_id}
                onChange={(value) => handleInputChange("category_id", value)}
                options={categoryList.map((category) => ({
                  value: category.id,
                  label: category.translations.en.name,
                }))}
                className="mt-4"
                placeholder={t("select_category_placeholder")}
              />
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 pt-4 pb-2 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              {t("cancel")}
            </button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              className="px-6"
              iconName="Save"
              iconPosition="left"
            >
              {mode === "create" ? t("createCouponButton") : t("editCoupon")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CouponModal;
